import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  PhotoIcon,
  ArrowTrendingUpIcon,
  UsersIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import { trackEvent } from '../lib/analytics';
import { track } from '../lib/track';

export default function ForCampOwnersPage() {
  return (
    <div className="page-container">
      <SEO
        title="For Camp Owners - Reach More Families | CampWhiz"
        description="Join CampWhiz to help families discover your camp faster. Claim your FREE listing, manage your details, and connect with parents planning their kids' activities."
        keywords="camp owner, camp listing, camp management, camp advertising, claim camp, camp directory, summer camp marketing"
        url="/for-owners"
      />

      <div className="page-content">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
              Where Families Find Your Camp
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
              CampWhiz is on a mission to make discovery easier for parents — and visibility effortless for camps.
            </p>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
              We’re building the most complete collection of national and local camps so families can explore every option in one place — no endless tabs or scattered searches.
            </p>
            <div className="text-2xl font-semibold text-primary mb-6">
              Claim. Manage. Connect.
            </div>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Every camp receives a basic listing for FREE. Be part of the trusted space where families plan their activities.
            </p>
          </div>

          {/* Steps Section */}
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Step 1 */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 flex flex-col min-h-[460px]">
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-primary text-white rounded-full w-14 h-14 flex items-center justify-center text-2xl font-bold">
                    1
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
                  Claim Your Camp (FREE)
                </h3>
                <p className="text-base text-gray-700 text-center mb-4">
                  Most camps are already listed — just find yours and claim it.
                </p>
                <div className="mt-2 mb-4 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                <div className="space-y-4 flex-1 pb-6">
                  <div className="grid grid-cols-[1.75rem,1fr] gap-3 items-start">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <ClipboardDocumentCheckIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Take ownership of your camp and verify your information
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.75rem,1fr] gap-3 items-start">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Earn a “Claimed &amp; Official” badge to show parents your listing is verified
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.75rem,1fr] gap-3 items-start">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <ArrowTrendingUpIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Ensure your camp appears accurately in local search results
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 text-center mt-auto">
                  If your camp isn’t listed yet,{' '}
                  <a href="#" className="text-primary font-semibold hover:text-primary/80 transition-colors">
                    share your details →
                  </a>{' '}
                  and we'll add it for FREE.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 flex flex-col min-h-[460px]">
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-primary text-white rounded-full w-14 h-14 flex items-center justify-center text-2xl font-bold">
                    2
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
                  Manage Your Listing (Upgrade for full control)
                </h3>
                <p className="text-base text-gray-700 text-center mb-4">
                  Once claimed, enhance your listing and reach more families.
                </p>
                <div className="mt-2 mb-4 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                <div className="space-y-4 flex-1 pb-6">
                  <div className="grid grid-cols-[1.75rem,1fr] gap-3 items-start">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <PhotoIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Add photos, logos, and detailed descriptions
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.75rem,1fr] gap-3 items-start">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <ClockIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Update hours, dates, and pricing anytime
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.75rem,1fr] gap-3 items-start">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <UsersIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Highlight programs and activities parents search for most
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.75rem,1fr] gap-3 items-start">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <ArrowTrendingUpIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Boost placement in search results with higher-tier plans
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 text-center mt-auto">
                  Want to explore plan options?{' '}
                  <Link
                    to="/pricing/owners"
                    onClick={() => {
                      trackEvent('cta_clicked', {
                        cta_location: 'for_owners_step_2',
                        cta_text: 'See plans and details',
                        destination: 'pricing_owners',
                        user_type: 'business',
                        page: 'for_owners'
                      });
                      track('cta_clicked', {
                        persona: 'camp_owner',
                        page: 'for_camp_owners',
                        cta_location: 'for_owners_step_2',
                        cta_text: 'See plans and details',
                        destination: 'pricing_owners',
                      });
                    }}
                    className="text-primary font-semibold hover:text-primary/80 transition-colors"
                  >
                    See plans and details →
                  </Link>
                </p>
              </div>

              {/* Step 3 */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 flex flex-col min-h-[460px]">
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-primary text-white rounded-full w-14 h-14 flex items-center justify-center text-2xl font-bold">
                    3
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
                  Connect with Parents
                </h3>
                <p className="text-base text-gray-700 text-center mb-4">
                  Become part of how families plan their kids’ activities all year long.
                </p>
                <div className="mt-2 mb-4 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                <div className="space-y-4 flex-1 pb-6">
                  <div className="grid grid-cols-[1.75rem,1fr] gap-3 items-start">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <UsersIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Be discovered by parents searching in your area
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.75rem,1fr] gap-3 items-start">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Build trust with verified, accurate details
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.75rem,1fr] gap-3 items-start">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <ClockIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Stay visible year-round — not just during registration season
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 text-center mt-auto">
                  CampWhiz connects your camp with parents where they’re already looking.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-primary to-purple-600 rounded-2xl p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Reach More Families?
            </h2>
            <p className="text-xl mb-4">
              Join the trusted space where parents plan their camps.
            </p>
            <p className="text-xl mb-8">
              Claim your FREE listing and upgrade when you're ready.
            </p>
            <Link
              to="/register?type=business"
              onClick={() => {
                trackEvent('cta_clicked', {
                  cta_location: 'for_owners_hero',
                  cta_text: 'Claim your camp',
                  destination: 'register',
                  user_type: 'business',
                  page: 'for_owners'
                });
                track('cta_clicked', {
                  persona: 'camp_owner',
                  page: 'for_camp_owners',
                  cta_location: 'for_owners_hero',
                  cta_text: 'Claim your camp',
                  destination: 'register',
                });
              }}
              className="inline-block bg-white text-primary px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors"
            >
              Claim your camp →
            </Link>
          </div>

          {/* Removal Section */}
          <div className="mt-12">
            <div className="bg-white border border-gray-200 rounded-2xl px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-left">
                <p className="text-base font-semibold text-gray-900">
                  Need to be removed?
                </p>
                <p className="text-sm text-gray-600 mt-1 max-w-xl">
                  Prefer not to appear on CampWhiz? Let us know and we’ll take care of it quickly, no questions asked.
                </p>
              </div>
              <a
                href="#"
                className="inline-flex items-center justify-center rounded-lg border border-primary text-primary px-5 py-2.5 text-sm font-semibold hover:bg-primary/5 transition-colors"
              >
                Request removal →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

